<template>
	<div class="teaming-scroller" ref="teamingScroller">
		<!--
		-- @see: https://github.com/Akryum/vue-virtual-scroller#dynamicscroller
		-->
		<DynamicScroller
			v-if="dynamicSize"
			ref="scroller"
			:class="classes"
			:items="items"
			:min-item-size="minItemSize"
			@scroll="$emit('scroll')"
		>
			<template v-slot="{ item, index, active }">
				<slot :item="item" :index="index" :active="active">
					{{ item }}
				</slot>
			</template>
		</DynamicScroller>
		<!--
		-- @see: https://github.com/Akryum/vue-virtual-scroller#recyclescroller
		-->
		<RecycleScroller
			v-else
			ref="scroller"
			:class="classes"
			:items="items"
			:item-size="itemSize"
			:key-field="keyField"
			v-slot="{ item }"
		>
			<slot :item="item">
				{{ item }}
			</slot>
		</RecycleScroller>
	</div>
</template>

<script>
import throttle from "lodash/throttle";

const SCROLL_THROTTLE = 5;
const SCROLL_THRESHOLD = 400;
const SCROLLER_CLASS_NAME = "teaming-scroller__virtual-component";

export default {
	name: "TeamingScroller",

	emits: [ "scroll", "scrollThresholdReached", ],

	props: {
		className: {
			type: String,
			default: "",
		},
		items: {
			type: Array,
			default: () => [],
		},
		itemSize: {
			type: Number,
			default: 16,
		},
		dynamicSize: {
			type: Boolean,
			default: false,
		},
		keyField: {
			type: String,
			default: "id",
		},
		isPagingEnabled: {
			type: Boolean,
			default: true,
		},
		minItemSize: {
			type: Number,
			default: 50,
		},
		pausePaging: {
			type: Boolean,
			default: false,
		},
		scrollThrottle: {
			type: Number,
			default: SCROLL_THROTTLE,
		},
		scrollThreshold: {
			type: Number,
			default: SCROLL_THRESHOLD,
		},
	},

	computed: {
		classes() {
			return `${ SCROLLER_CLASS_NAME } ${ this.className }`.trim();
		},
	},

	methods: {
		setUpScrollHandle() {
			this.disposeScrollHandle();
			const containerElement = this.$refs.teamingScroller?.getElementsByClassName( SCROLLER_CLASS_NAME )?.[ 0 ];
			if ( containerElement && !this.onComponentScroll ) {
				// assigning method here b/c we need to throttle it while also maintaining the call site context (this)
				this.onComponentScroll = throttle( e => {
					if ( this.pausePaging ) {
						return;
					}
					const { scrollHeight, scrollTop, clientHeight, } = e.target;
					const howManyPixelsAreLeftToScroll = scrollHeight - scrollTop - clientHeight;

					// UNCOMMENT for debugging
					// console.info( "SCROLL", {
					// 	howManyPixelsAreLeftToScroll,
					// 	scrollHeight,
					// 	scrollTop,
					// 	clientHeight,
					// 	scrollThreshold: this.scrollThreshold,
					// } );

					if ( howManyPixelsAreLeftToScroll < this.scrollThreshold ) {
						this.$emit( "scrollThresholdReached" );
					}
				}, this.scrollThrottle );

				containerElement.addEventListener( "scroll", this.onComponentScroll );
			}
		},
		disposeScrollHandle() {
			const containerElement = this.$refs.teamingScroller?.getElementsByClassName( SCROLLER_CLASS_NAME )[ 0 ];
			if ( !( containerElement && this.onComponentScroll ) ) {
				return;
			}
			containerElement.removeEventListener( "scroll", this.onComponentScroll );
		},

		//
		// public API
		//
		// eslint-disable-next-line vue/no-unused-properties
		scrollToItem( index ) {
			if ( this.$refs.scroller ) {
				this.$refs.scroller.scrollToItem( index );
			}
		},
	},

	mounted() {
		if ( !this.isPagingEnabled ) {
			return;
		}
		this.setUpScrollHandle();
	},

	updated() {
		if ( !this.isPagingEnabled ) {
			this.disposeScrollHandle();
		}
	},

	beforeUnmount() {
		this.disposeScrollHandle();
	},
};
</script>

<style lang="scss" scoped>
.teaming-scroller {
	height: 100%;
	overflow-y: hidden;
	position: relative;
}

.teaming-scroller__virtual-component {
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	width: 100%;
}
</style>
